/**
 * VoiceRecorder — minimal voice note recorder using MediaRecorder.
 * Returns a Blob (audio/webm or audio/mp4 depending on browser) when stopped.
 * Falls back gracefully if MediaRecorder isn't supported.
 */
export async function createVoiceRecorder() {
  if (!navigator.mediaDevices || !window.MediaRecorder) {
    throw new Error('Voice recording not supported on this device');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  let mimeType = '';
  for (const m of mimeCandidates) {
    if (!m || MediaRecorder.isTypeSupported(m)) { mimeType = m; break; }
  }
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
  const startedAt = Date.now();

  return {
    recorder,
    start() { recorder.start(); },
    stop() {
      return new Promise((resolve) => {
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
          const duration = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
          const ext = (blob.type.includes('mp4') ? 'm4a' : 'webm');
          const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
          resolve({ blob, file, duration });
        };
        recorder.stop();
      });
    },
    cancel() {
      try { recorder.stop(); } catch {/* ignore */}
      stream.getTracks().forEach((t) => t.stop());
    },
    isRecording() {
      return recorder.state === 'recording';
    },
  };
}
